"use client";
import React, { useEffect, useMemo, useState } from "react";

import Image from "next/image";
import { Alert, Box, Container, Skeleton, SxProps, Theme, Typography } from "@mui/material";

import DateIcon from "@mui/icons-material/TodayRounded";
import LocationIcon from "@mui/icons-material/FmdGoodOutlined";
import ShipIcon from "@mui/icons-material/DirectionsBoatFilledRounded";
import AdsClickIcon from "@mui/icons-material/AdsClickRounded";

import { IAllCruisesList, ICruise } from "@/interfaces/IAllCruisesList";
import { convertToDateV1 } from "@/helpers/helpers";

import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/pagination";
import { ICruisesByRegions } from "./BookOnlineCruiseDeals";
import { toKebabCase } from "@/utils/helpers";
import { fetchMoreCruises } from "../cruise-results/miniComponents/loadmore_request";
import { LinkButtonV2 } from "./BestCruiseDeals";

const LuxuryCruiseDeals = () => {
  // Changed to object to avoid reference equality issues
  const [regionCruisesMap, setRegionCruisesMap] = useState<Record<string, ICruise[]>>({});
  const [loadingRegions, setLoadingRegions] = useState<string[]>([]);

  useEffect(() => {
    const regions = ["Transatlantic", "Panama Canal", "Alaska", "Middle East", "West Coast US"];
    let isMounted = true;

    const fetchRegionSequentially = async () => {
      // Initialize loading state for all regions
      if (isMounted) {
        setLoadingRegions(regions);
      }

      for (const region of regions) {
        try {
          const apiParams = {
            region: region,
            limit: 15,
            page: 1,
            profileImageGeometry: "300x",
            coverImageGeometry: "800x",
          };

          const response: IAllCruisesList = await fetchMoreCruises(apiParams);

          if (isMounted) {
            setRegionCruisesMap((prevMap) => ({
              ...prevMap,
              [region]: response.cruises || [],
            }));

            setLoadingRegions((prevLoading) => prevLoading.filter((r) => r !== region));
          }
        } catch (err) {
          console.log(`Error fetching ${region}:`, err);

          if (isMounted) {
            setLoadingRegions((prevLoading) => prevLoading.filter((r) => r !== region));
          }
        }
      }
    };

    fetchRegionSequentially();
    return () => {
      isMounted = false;
    };
  }, []);

  const luxuryCruises = useMemo(() => {
    const allCruises: ICruise[] = Object.values(regionCruisesMap).flat();
    return allCruises.filter((cruise) => cruise.rating === "Luxury");
  }, [regionCruisesMap]);

  const luxuryCruiseParts = useMemo(() => {
    if (luxuryCruises.length === 0) return { minimum: [], middle: [], higher: [] };

    // Get minimum price for each cruise
    const cruisesWithMinPrice = luxuryCruises
      .map((cruise) => {
        const prices = extractPricesFromCruise(cruise);
        const minPrice = prices.length > 0 ? Math.min(...prices.map((p) => p.price)) : 0;
        return { ...cruise, minPrice, allPrices: prices };
      })
      .filter((cruise) => cruise.minPrice > 0);

    // Sort by minimum price
    const sortedCruises = cruisesWithMinPrice.sort((a, b) => a.minPrice - b.minPrice);

    const parts = Math.ceil(sortedCruises.length / 3);

    return {
      minimum: sortedCruises.slice(0, parts),
      middle: sortedCruises.slice(parts, parts * 2),
      higher: sortedCruises.slice(parts * 2),
    };
  }, [luxuryCruises]);

  const luxuryCruiseResult = useMemo(() => {
    const { minimum, middle, higher } = luxuryCruiseParts;
    const totalCruises = minimum.length + middle.length + higher.length;

    let combinedCruises = [];

    if (totalCruises <= 2) {
      combinedCruises = [...minimum, ...middle, ...higher];
    } else if (totalCruises <= 4) {
      combinedCruises = [
        ...minimum.slice(0, 1),
        ...middle.slice(0, 1),
        ...higher.slice(0, 1),
        ...minimum.slice(1, 2),
        ...middle.slice(1, 2),
      ];
    } else {
      combinedCruises = [...minimum.slice(0, 2), ...middle.slice(0, 2), ...higher.slice(0, 2)];
    }

    // Remove any undefined/empty entries
    const validCruises = combinedCruises.filter((cruise) => cruise != null);

    // Fix the logic
    const cruiseCount = validCruises.length;
    let showCruise;

    if (cruiseCount >= 6) {
      showCruise = validCruises.slice(0, 6);
    } else if (cruiseCount >= 4) {
      showCruise = validCruises.slice(0, 4);
    } else {
      showCruise = validCruises.slice(0, 2);
    }

    return showCruise.sort((a, b) => a.minPrice - b.minPrice);
  }, [luxuryCruiseParts]);

  const isLoading = loadingRegions.length > 0;
  const hasNoResults = !isLoading && luxuryCruiseResult?.length === 0;

  return (
    luxuryCruiseResult?.length > 0 && (
      <Box
        sx={{
          mt: { xs: "1.2rem", md: "4rem" },
          overflow: { xs: "hidden", md: "visible" },
          mb: { xs: "3rem", md: "5rem" },
        }}
      >
        <Container>
          <Box sx={{ position: "relative" }}>
            <Box sx={{ position: "relative", zIndex: 2 }}>
              <Box sx={{ mt: "1rem", mb: "1.5rem" }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "2.2rem", md: "3rem" },
                    fontWeight: { xs: 800, md: 700 },
                    color: "#fff",
                    lineHeight: { xs: 1.1, md: 1.3 },
                    mb: { xs: "10px", md: "0px" },
                    span: {
                      color: "transparent",
                      WebkitTextStrokeColor: "#fff",
                      WebkitTextStrokeWidth: "1px",
                    },
                  }}
                >
                  Luxury Cruise{" "}
                  <span>
                    Deals {new Date().getFullYear()}/
                    {String(new Date().getFullYear() + 1).slice(-2)}
                  </span>
                </Typography>
                <Box
                  sx={{
                    fontSize: { xs: "14px", md: "1rem" },
                    fontWeight: 400,
                    lineHeight: { xs: 1.4, md: 1.3 },
                    color: "#fff",
                  }}
                >
                  Discover exclusive luxury cruise deals, promotions, and special offers from the
                  world&apos;s leading cruise lines, all in one place
                </Box>
              </Box>

              <Box sx={{ display: { xs: "none", md: "block" } }}>
                {luxuryCruiseResult?.length > 0 ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: "20px",
                      "> div": {
                        width: "100%",
                        height: "100%",
                      },
                    }}
                  >
                    {/* Show actual cruise cards */}
                    {luxuryCruiseResult.slice(0, 6).map((cruise, cI) => (
                      <BestCruiseCard key={cI} image={(cI + 1)?.toString()} cruise={cruise} />
                    ))}

                    {/* Show skeleton for remaining slots while loading */}
                    {luxuryCruiseResult.length < 6 &&
                      isLoading &&
                      Array.from({ length: 6 - luxuryCruiseResult.length }).map(
                        (_, skeletonIndex) => (
                          <Box
                            key={`skeleton-${skeletonIndex}`}
                            sx={{
                              height: "218px",
                              width: "100%",
                              position: "relative",
                            }}
                          >
                            <Skeleton
                              variant="rectangular"
                              animation="wave"
                              sx={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "15px",
                              }}
                            />
                          </Box>
                        ),
                      )}
                  </Box>
                ) : (
                  isLoading && (
                    // Show all skeletons when no results yet
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: "20px",
                        "> div": {
                          width: "100%",
                          height: "100%",
                          minHeight: "200px",
                        },
                      }}
                    >
                      {Array.from({ length: 2 }).map((_, xi) => (
                        <Box
                          key={xi}
                          sx={{
                            height: "218px",
                            width: "100%",
                            position: "relative",
                          }}
                        >
                          <Skeleton
                            variant="rectangular"
                            animation="wave"
                            sx={{
                              width: "100%",
                              height: "100%",
                              borderRadius: "15px",
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )
                )}
                {hasNoResults && (
                  <Box>
                    <Alert severity="error">No data available. Please contact our support</Alert>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: { xs: "block", md: "none" } }}>
                {luxuryCruiseResult?.length > 0 ? (
                  <Box
                    sx={{
                      position: "relative",
                      width: { xs: "100%", sm: "100%" },
                      ".swiper": {
                        width: "100%",
                        overflow: { xs: "visible", sm: "hidden" },
                      },
                    }}
                  >
                    <Swiper
                      spaceBetween={16}
                      slidesPerView={4}
                      breakpoints={{
                        0: {
                          slidesPerView: 1.1,
                        },
                        600: {
                          slidesPerView: 2,
                        },
                        900: {
                          slidesPerView: 3,
                        },
                        1200: {
                          slidesPerView: 4,
                        },
                      }}
                    >
                      {luxuryCruiseResult.slice(0, 6).map((cruise, cI) => (
                        <SwiperSlide key={cI}>
                          <BestCruiseCard image={(cI + 1)?.toString()} cruise={cruise} />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </Box>
                ) : (
                  <Box>No data available</Box>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: "#060273",
                position: "absolute",
                top: { xs: "-14px", lg: "-1.25rem", xl: "-1.5rem" },
                left: { xs: "-16px", sm: "-24px", md: "-24px", lg: "-1rem", xl: "-2.5rem" },
                width: {
                  xs: "calc(100% + 32px)",
                  sm: "calc(100% + 48px)",
                  md: "calc(100% + 3rem)",
                  lg: "calc(100% + 2rem)",
                  xl: "calc(100% + 5rem)",
                },
                height: {
                  xs: "calc(100% + 32px)",
                  lg: "calc(100% + 2.5rem)",
                  xl: "calc(100% + 3.5rem)",
                },
                zIndex: 1,
                borderRadius: { xs: "0px", lg: "35px" },
              }}
            ></Box>
          </Box>
        </Container>
      </Box>
    )
  );
};

export default LuxuryCruiseDeals;

const BestCruiseCard: React.FC<{
  cruise: ICruise & { region?: string };
  isLargeCard?: boolean;
  image: string;
  sx?: SxProps<Theme>;
}> = ({ cruise, sx }) => {
  const priceInfo = useMemo(() => {
    return getLowestPriceInfo(cruise);
  }, [cruise]);
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: "#fff",
        p: { xs: "0px", md: "16px" },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr", lg: "250px auto", xl: "300px auto" },
        gap: { xs: "0px", md: "1rem" },
        borderRadius: { xs: "20px", md: "24px" },
        ...sx,
      }}
    >
      <Box
        sx={{
          width: "100%",
          minHeight: { xs: "220px", md: "200px" },
          height: { xs: "200px", md: "auto" },
          borderRadius: { xs: "15px 15px 0 0", md: "15px" },
          overflow: "hidden",
          position: "relative",
          img: {
            objectFit: "cover",
            height: "100%",
            width: "100%",
            position: "absolute",
            inset: "0px",
          },
        }}
      >
        {cruise?.shipImageHref && (
          <Image
            src={cruise?.shipImageHref}
            alt={cruise?.shipTitle}
            width={50}
            height={10}
            unoptimized
            className="panning_image"
          />
        )}

        <Box
          sx={{
            p: "5px 11px",
            bgcolor: "#E30053",
            color: "#fff",
            textTransform: "capitalize",
            width: "fit-content",
            fontFamily: "'blauerNue', 'blauerNue Fallback'",
            fontWeight: 600,
            fontSize: "14px",
            borderRadius: "15px",
            position: "absolute",
            bottom: "10px",
            left: "10px",
            pointerEvents: "none",
          }}
        >
          {cruise?.cruiseType} Cruise
        </Box>

        {/* Region badge */}
        {cruise?.region && (
          <Box
            sx={{
              p: "5px 11px",
              bgcolor: "#7D46C9",
              color: "#fff",
              textTransform: "capitalize",
              width: "fit-content",
              fontFamily: "'blauerNue', 'blauerNue Fallback'",
              fontWeight: 600,
              fontSize: "12px",
              borderRadius: "15px",
              position: "absolute",
              top: "10px",
              right: "10px",
            }}
          >
            {cruise.region}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          minHeight: { xs: "295px", md: "auto" },
          p: { xs: "1rem", md: "0px" },
        }}
      >
        <Box>
          <Box
            sx={{
              fontSize: { xs: "1.4rem", md: "1.4rem", lg: "1.4rem" },
              fontFamily: "'blauerNue', 'blauerNue Fallback'",
              fontWeight: { xs: 700, md: 700 },
              mb: "10px",
            }}
          >
            {cruise.name}
            {"  "}
            <Box
              component="span"
              sx={{
                bgcolor: "#FFBB00",
                color: "#350097",
                fontSize: "14px",
                p: "3px 5px 3px 4px",
                textTransform: "uppercase",
                borderRadius: "6px",
                border: "1px solid #5900FF80",
                ml: "0px",
                position: "relative",
                top: "-3px",
              }}
            >
              {cruise?.cruiseNights} Nights
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: "10px",
            }}
          >
            <Box>
              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#7D46C9",
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  mb: "4px",
                }}
              >
                Departing From
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "3px",
                  fontSize: "14px",
                  color: "#666",
                  fontWeight: 400,
                }}
              >
                <LocationIcon sx={{ width: "20px", height: "20px", ml: "-3px" }} />
                <Box sx={{ mt: "2px" }}>{cruise?.startsAt}</Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: "flex", gap: "4px", color: "#7D46C9" }}>
                <Box>
                  <Box
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#7D46C9",
                      fontFamily: "'blauerNue', 'blauerNue Fallback'",
                      mb: "4px",
                    }}
                  >
                    Departing On
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "3px",
                      fontSize: "14px",
                      color: "#666",
                      fontWeight: 400,
                    }}
                  >
                    <DateIcon sx={{ width: "20px", height: "20px", ml: "-2px" }} />
                    <Box sx={{ mt: "2px" }}>{convertToDateV1(cruise?.startsOn)}</Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box>
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#7D46C9",
                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                mb: "4px",
                mt: "8px",
              }}
            >
              Sailing on
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                fontSize: "14px",
                gap: "3px",
                color: "#666",
                ml: "-2px",
                mb: "2px",
                svg: {
                  width: "20px",
                  height: "20px",
                  color: "#555",
                },
                span: {
                  fontWeight: 700,
                },
              }}
            >
              <ShipIcon />
              {cruise?.shipTitle} by <span>{cruise?.operatorTitle}</span>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            width: "100%",
            borderTop: "1px solid #ccc",
            pt: "2px",
            mt: "8px",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                fontSize: { xs: "12px", md: "14px" },
                fontWeight: 700,
                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                mt: "8px",
                lineHeight: 1.2,
              }}
            >
              PRICE FROM
            </Box>
            <Typography
              variant="body2"
              sx={{
                mt: "-3px",
                color: "#E30053",
                fontSize: "25px",
                fontWeight: 600,
                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                lineHeight: 1.1,
                span: {
                  color: "#A8215C",
                  fontSize: "22px",
                },
                ".gbp": {
                  mr: "2px",
                },
                ".pp": {
                  ml: "2px",
                },
              }}
            >
              <span className="gbp">£</span>
              {priceInfo.price}
              <span className="pp">pp</span>

              {priceInfo.displayName && (
                <Box
                  component={"span"}
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    position: "absolute",
                    top: { xs: "10px", md: "9px" },
                    left: { xs: "88px", md: "95px" },
                    width: "max-content",
                    color: "#060273",
                  }}
                >
                  {priceInfo.displayName}
                </Box>
              )}
            </Typography>
          </Box>

          <LinkButtonV2
            href={`/cruise-results/${toKebabCase(cruise?.name ?? "")}?cruiseRef=${
              cruise?.ref
            }&shipRef=${cruise?.ship?.split("/").pop()?.split(".")[0] ?? ""}`}
            text=" View Itinerary"
            icon={
              <AdsClickIcon
                className="zoom-icon"
                sx={{ display: { xs: "none", md: "inline-block" } }}
              />
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

const extractPricesFromCruise = (cruise: ICruise) => {
  return [
    { price: Number(cruise.cruiseOnlyPrice), type: "Cruise Only", displayName: "" },
    { price: Number(cruise.flyCruisePrice), type: "Fly Cruise", displayName: "(with Flight)" },
    { price: Number(cruise.insidePrice), type: "Inside Cabin", displayName: "(Inside Cabin)" },
    { price: Number(cruise.outsidePrice), type: "Outside Cabin", displayName: "(Outside Cabin)" },
    { price: Number(cruise.balconyPrice), type: "Balcony", displayName: "(Balcony)" },
    { price: Number(cruise.suitePrice), type: "Suite", displayName: "(Suite)" },
  ].filter((p) => p.price > 0 && !isNaN(p.price));
};

const getLowestPriceInfo = (cruise: ICruise) => {
  const prices = extractPricesFromCruise(cruise);
  if (prices.length === 0) {
    return { price: "0.00", type: "", displayName: "" };
  }
  const sortedPrices = [...prices].sort((a, b) => a.price - b.price);
  const lowest = sortedPrices[0];
  return {
    price: lowest.price.toFixed(2),
    type: lowest.type,
    displayName: lowest.displayName,
  };
};

export const getMinimumPriceDealPerRegion = (cruisesByRegions: ICruisesByRegions) => {
  const minDealsPerRegion: Array<
    ICruise & {
      region: string;
      lowestPrice: number;
      lowestPriceType: string;
      lowestPriceDisplayName: string;
      allPrices: Array<{ price: number; type: string; displayName: string }>;
    }
  > = [];

  Object.entries(cruisesByRegions).forEach(([region, cruises]) => {
    if (!cruises || cruises.length === 0) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let regionMinDeal: any = null;
    let regionMinPrice = Infinity;

    cruises?.forEach((cruise) => {
      const allPrices = extractPricesFromCruise(cruise);
      if (allPrices.length > 0) {
        const sortedPrices = [...allPrices].sort((a, b) => a.price - b.price);
        const lowestPriceObj = sortedPrices[0];

        if (lowestPriceObj.price < regionMinPrice) {
          regionMinPrice = lowestPriceObj.price;
          regionMinDeal = {
            ...cruise,
            region,
            lowestPrice: lowestPriceObj.price,
            lowestPriceType: lowestPriceObj.type,
            lowestPriceDisplayName: lowestPriceObj.displayName,
            allPrices: sortedPrices,
          };
        }
      }
    });
    if (regionMinDeal) {
      minDealsPerRegion.push(regionMinDeal);
    }
  });
  return minDealsPerRegion.sort((a, b) => a.lowestPrice - b.lowestPrice);
};
